import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Building2, Check, ChevronRight } from 'lucide-react';
import { cn } from '@/shared/cn';
import { MENU_SUB_CONTENT } from '@/shared/ui-class';
import { useBrand, type BrandId } from './BrandContext';

// =========================================================================
// BrandMenuItem — "OEM identity" row in the AccountMenu, beside Theme.
// Fork-owned. Meridian (fictional, pink machines) is the default; Komatsu is
// for Komatsu-only sessions. The same switch lives in Settings → Appearance.
//
// Mirrors AccountMenu's own ThemeSubmenu exactly: a trigger row that toggles a
// portalled fixed-position panel anchored to the trigger's right edge. The
// portal is what keeps the submenu above the sidebar's overflow clipping, so
// this file deliberately copies that mechanism rather than nesting a panel.
//
// AccountMenu mounts this with a two-line seam; everything else lives here.
// =========================================================================

function BrandSubmenu({
  open,
  anchorRef,
  panelRef,
  currentBrand,
  onSelect,
}: {
  open: boolean;
  anchorRef: React.RefObject<HTMLButtonElement>;
  panelRef: React.Ref<HTMLDivElement>;
  currentBrand: BrandId;
  onSelect: (id: BrandId) => void;
}) {
  const { brands } = useBrand();
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return;
    const update = () => {
      const rect = anchorRef.current!.getBoundingClientRect();
      setCoords({ top: rect.top, left: rect.right + 6 });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, anchorRef]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={panelRef}
      className={cn(MENU_SUB_CONTENT, 'fixed z-[200] w-[180px]')}
      style={{ top: coords.top, left: coords.left }}
      role="menu"
    >
      {brands.map((b) => (
        <button
          key={b.id}
          type="button"
          role="menuitemradio"
          aria-checked={currentBrand === b.id}
          className="flex w-full cursor-default items-center gap-2 rounded-md px-2 py-1.5 text-sm text-zinc-700 outline-none hover:bg-zinc-100 dark:text-zinc-200 dark:hover:bg-zinc-800"
          onClick={() => onSelect(b.id)}
        >
          <span
            aria-hidden="true"
            className="h-3 w-3 flex-shrink-0 rounded-full"
            style={{ background: b.accentHex }}
          />
          {b.lockup}
          {currentBrand === b.id ? (
            <Check className="ml-auto h-3.5 w-3.5 text-zinc-500" />
          ) : null}
        </button>
      ))}
    </div>,
    document.body,
  );
}

/**
 * Renders the trigger row plus its portalled submenu.
 *
 * @param onSelected called after a brand is chosen, so the host menu can close.
 * @param panelRef   give AccountMenu's click-outside handler a ref to the
 *                   portalled panel; without it, clicking the submenu closes
 *                   the whole account menu.
 */
export function BrandMenuItem({
  onSelected,
  panelRef,
}: {
  onSelected?: () => void;
  panelRef?: React.Ref<HTMLDivElement>;
}) {
  const { brand, setBrand } = useBrand();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  return (
    <div>
      <button
        ref={triggerRef}
        type="button"
        className="flex w-full cursor-default items-center gap-2 rounded-md px-2 py-1.5 text-sm text-zinc-700 outline-none hover:bg-zinc-100 dark:text-zinc-200 dark:hover:bg-zinc-800"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Building2 className="h-4 w-4 text-zinc-500" />
        OEM identity
        <span className="ml-auto text-xs text-muted">{brand.lockup}</span>
        <ChevronRight className="h-3.5 w-3.5 text-zinc-400" />
      </button>
      <BrandSubmenu
        open={open}
        anchorRef={triggerRef}
        panelRef={panelRef ?? null}
        currentBrand={brand.id}
        onSelect={(id) => {
          setBrand(id);
          setOpen(false);
          onSelected?.();
        }}
      />
    </div>
  );
}
